import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trainingAPI, camerasAPI, getThumbnailUrl, getAlertImageUrl } from '../utils/api';
import { formatDateTime } from '../utils/formatDateTime';
import EmptyState from '../components/ui/EmptyState';
import ImageModal, { AlertThumbnail } from '../components/ImageModal';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  NoSymbolIcon,
  ShieldCheckIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';

// Phase 1 of the per-camera training feature: review the recent vest alerts for a
// camera and label each as a real violation (CORRECT) or a missed-vest false
// positive (FALSE_POSITIVE = the vest was actually present). View-only models list.
// No training-trigger / deploy actions yet — those are Phase 2/3.
const PPE_TYPE = 'vest';
const PAGE_LIMIT = 50;

const formatConfidence = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return `${Math.round((num <= 1 ? num : num / 100) * 100)}%`;
};

export default function CameraTrainingPage() {
  const params = useParams();
  const cameraId = params.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [onlyUnlabeled, setOnlyUnlabeled] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);

  const itemsParams = { ppeType: PPE_TYPE, limit: PAGE_LIMIT, onlyUnlabeled };

  // Camera meta (name/location) for the header — reuse the shared cameras endpoint.
  const { data: cameraData } = useQuery({
    queryKey: ['camera', cameraId],
    queryFn: () => camerasAPI.getCamera(cameraId),
    enabled: !!cameraId,
    staleTime: 300000,
  });
  const camera = cameraData?.data?.data;

  const { data: itemsResp, isLoading: itemsLoading, isFetching: itemsFetching, refetch } = useQuery({
    queryKey: ['training-items', cameraId, itemsParams],
    queryFn: () => trainingAPI.getItems(cameraId, itemsParams),
    enabled: !!cameraId,
  });
  const itemsPayload = itemsResp?.data || {};
  const items = itemsPayload.items || [];
  const cameraName = itemsPayload.camera?.name || camera?.name || 'Camera';

  const { data: statsResp } = useQuery({
    queryKey: ['training-stats', cameraId],
    queryFn: () => trainingAPI.getStats(cameraId),
    enabled: !!cameraId,
  });
  const stats = statsResp?.data || {};
  const vestStats = stats.byPpeType?.[PPE_TYPE] || { positive: 0, negative: 0, total: 0 };
  const cleanCoverage = stats.samplesWithCleanImage ?? 0;

  const { data: modelsResp } = useQuery({
    queryKey: ['training-models', cameraId],
    queryFn: () => trainingAPI.getModels(cameraId),
    enabled: !!cameraId,
  });
  const models = modelsResp?.data?.models || [];

  // Phase 2: training runs. Poll while any run is in flight so status/metrics update live.
  const [modelType, setModelType] = useState('CLASSIFIER');
  const [host, setHost] = useState('DEV_BOX');

  const { data: runsResp } = useQuery({
    queryKey: ['training-runs', cameraId],
    queryFn: () => trainingAPI.getRuns(cameraId),
    enabled: !!cameraId,
    refetchInterval: (query) => {
      const runs = query.state.data?.data?.runs || [];
      const active = runs.some((r) => r.status === 'PENDING' || r.status === 'RUNNING');
      return active ? 5000 : false;
    },
  });
  const runs = runsResp?.data?.runs || [];
  const hasActiveRun = runs.some((r) => r.status === 'PENDING' || r.status === 'RUNNING');

  const startRunMutation = useMutation({
    mutationFn: () => trainingAPI.startRun(cameraId, { modelType, host, ppeType: PPE_TYPE }),
    onSuccess: () => {
      toast.success('Training run started');
      queryClient.invalidateQueries({ queryKey: ['training-runs', cameraId] });
    },
    onError: (err) => {
      const data = err?.response?.data;
      toast.error(data?.error || data?.message || 'Failed to start training run');
    },
  });

  // Phase 3: model deploy controls. Promote (SHADOW→ACTIVE; backend auto-archives the
  // previously-active model for this camera/type) and Archive (→ARCHIVED).
  const promoteModelMutation = useMutation({
    mutationFn: (modelId) => trainingAPI.promoteModel(modelId),
    onSuccess: () => {
      toast.success('Model promoted to ACTIVE');
      queryClient.invalidateQueries({ queryKey: ['training-models', cameraId] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || err.message);
    },
  });

  const archiveModelMutation = useMutation({
    mutationFn: (modelId) => trainingAPI.archiveModel(modelId),
    onSuccess: () => {
      toast.success('Model archived');
      queryClient.invalidateQueries({ queryKey: ['training-models', cameraId] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || err.message);
    },
  });

  const handlePromoteModel = useCallback((modelId) => {
    if (!window.confirm('Promote this model to ACTIVE for the camera? It will start affecting live detection.')) {
      return;
    }
    promoteModelMutation.mutate(modelId);
  }, [promoteModelMutation]);

  const labelMutation = useMutation({
    mutationFn: (body) => trainingAPI.labelSample(cameraId, body),
    onSuccess: (_, body) => {
      toast.success(
        body.label === 'FALSE_POSITIVE'
          ? 'Labeled: vest present (false positive)'
          : 'Labeled: real violation'
      );
      queryClient.invalidateQueries({ queryKey: ['training-items', cameraId] });
      queryClient.invalidateQueries({ queryKey: ['training-stats', cameraId] });
    },
    onError: () => {
      toast.error('Failed to save label');
    },
  });

  const labelItem = useCallback((item, label) => {
    labelMutation.mutate({ alertId: item.alertId, label, ppeType: PPE_TYPE });
  }, [labelMutation]);

  // Keyboard niceties while the lightbox is open: F = false positive (vest present),
  // R = real violation. Mirrors the AlertsPage review flow.
  useEffect(() => {
    if (!viewingItem) return;
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const key = e.key.toLowerCase();
      if (key === 'f') {
        labelItem(viewingItem, 'FALSE_POSITIVE');
      } else if (key === 'r') {
        labelItem(viewingItem, 'CORRECT');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [viewingItem, labelItem]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/cameras/${cameraId}/config`)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Training Data</h1>
            <p className="text-gray-500">{cameraName}{camera?.location ? ` - ${camera.location}` : ''}</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="btn btn-secondary flex items-center gap-2"
        >
          <ArrowPathIcon className="w-4 h-4" />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Real violations" value={vestStats.negative} dot="bg-red-500" />
        <StatCard label="Vest present (FP)" value={vestStats.positive} dot="bg-emerald-500" />
        <StatCard label="Total labeled" value={vestStats.total} dot="bg-blue-500" />
        <StatCard label="With clean image" value={cleanCoverage} dot="bg-slate-500" />
      </div>

      {/* Train model */}
      <div className="card">
        <div className="p-4 flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-500">Model type</label>
            <select
              value={modelType}
              onChange={(e) => setModelType(e.target.value)}
              className="rounded-lg border-slate-300 text-sm"
            >
              <option value="CLASSIFIER">Classifier</option>
              <option value="DETECTOR">Detector</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-500">Host</label>
            <select
              value={host}
              onChange={(e) => setHost(e.target.value)}
              className="rounded-lg border-slate-300 text-sm"
            >
              <option value="DEV_BOX">Dev box</option>
              <option value="CLOUD_GPU">Cloud GPU</option>
              <option value="EDGE">Edge</option>
            </select>
          </div>
          <button
            onClick={() => startRunMutation.mutate()}
            disabled={startRunMutation.isPending || hasActiveRun}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
            title={hasActiveRun ? 'A training run is already in progress' : 'Start a training run'}
          >
            <CpuChipIcon className="w-4 h-4" />
            {startRunMutation.isPending ? 'Starting…' : hasActiveRun ? 'Run in progress…' : 'Train'}
          </button>
        </div>
      </div>

      {/* Review queue */}
      <div className="card">
        <div className="p-4 border-b flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-semibold text-slate-900">Review queue</h3>
            <p className="text-sm text-slate-500">
              Recent {PPE_TYPE} alerts — {itemsPayload.count ?? items.length} shown
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlyUnlabeled}
              onChange={(e) => setOnlyUnlabeled(e.target.checked)}
              className="rounded border-slate-300"
            />
            Only unlabeled
          </label>
        </div>

        <div className={clsx('transition-opacity duration-200', itemsFetching && !itemsLoading && 'opacity-50 pointer-events-none')}>
          {itemsLoading ? (
            <EmptyState variant="loading" title="Loading review queue…" />
          ) : items.length === 0 ? (
            <EmptyState
              variant="success"
              icon={ShieldCheckIcon}
              title={onlyUnlabeled ? 'Nothing left to label' : 'No alerts to review'}
              description={onlyUnlabeled
                ? 'Every recent vest alert for this camera has been labeled.'
                : 'No recent vest alerts found for this camera.'}
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {items.map((item) => (
                <ReviewCard
                  key={item.alertId}
                  item={item}
                  onLabel={(label) => labelItem(item, label)}
                  onViewImage={() => setViewingItem(item)}
                  isPending={labelMutation.isPending && labelMutation.variables?.alertId === item.alertId}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Models (read-only) */}
      <div className="card">
        <div className="p-4 border-b flex items-center gap-2">
          <CpuChipIcon className="w-5 h-5 text-slate-500" />
          <h3 className="font-semibold text-slate-900">Models</h3>
        </div>
        {models.length === 0 ? (
          <EmptyState
            variant="empty"
            icon={CpuChipIcon}
            title="No camera-specific models yet"
            description="A model will appear here once training has run for this camera."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="glass-table w-full">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Type</th>
                  <th>PPE</th>
                  <th>Stage</th>
                  <th>Confidence</th>
                  <th>Metrics</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => {
                  const stage = (m.stage || '').toUpperCase();
                  const busy = promoteModelMutation.isPending || archiveModelMutation.isPending;
                  return (
                    <tr key={m.id}>
                      <td className="font-medium text-slate-800">{m.version || '—'}</td>
                      <td className="text-slate-600">{m.modelType || '—'}</td>
                      <td className="text-slate-600">{m.ppeType || '—'}</td>
                      <td><StageBadge stage={m.stage} /></td>
                      <td className="text-slate-600">{m.confThreshold != null ? formatConfidence(m.confThreshold) : '—'}</td>
                      <td className="text-slate-600">{formatMetrics(m.metrics)}</td>
                      <td className="text-slate-500 text-sm">{m.createdAt ? formatDateTime(m.createdAt) : '—'}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          {stage === 'SHADOW' && (
                            <button
                              onClick={() => handlePromoteModel(m.id)}
                              disabled={busy}
                              className="btn btn-primary btn-sm disabled:opacity-50"
                              title="Promote this model to ACTIVE"
                            >
                              Promote
                            </button>
                          )}
                          {(stage === 'ACTIVE' || stage === 'SHADOW') && (
                            <button
                              onClick={() => archiveModelMutation.mutate(m.id)}
                              disabled={busy}
                              className="btn btn-secondary btn-sm disabled:opacity-50"
                              title="Archive this model"
                            >
                              Archive
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Training runs */}
      <div className="card">
        <div className="p-4 border-b flex items-center gap-2">
          <ArrowPathIcon className="w-5 h-5 text-slate-500" />
          <h3 className="font-semibold text-slate-900">Training runs</h3>
        </div>
        {runs.length === 0 ? (
          <EmptyState
            variant="empty"
            icon={CpuChipIcon}
            title="No training runs yet"
            description="Start a run with the Train button above to fine-tune a model for this camera."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="glass-table w-full">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Host</th>
                  <th>Samples</th>
                  <th>Metrics</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id}>
                    <td><RunStatusBadge status={run.status} /></td>
                    <td className="text-slate-600">{run.modelType || '—'}</td>
                    <td className="text-slate-600">{run.host || '—'}</td>
                    <td className="text-slate-600">
                      {run.sampleCount ?? '—'}
                      {(run.posCount != null || run.negCount != null) && (
                        <span className="text-slate-400 text-sm"> ({run.posCount ?? 0}+ / {run.negCount ?? 0}-)</span>
                      )}
                    </td>
                    <td className="text-slate-600">
                      {run.status === 'FAILED' && run.error
                        ? <span className="text-red-600 text-sm">{run.error}</span>
                        : formatMetrics(run.metrics)}
                    </td>
                    <td className="text-slate-500 text-sm">{run.createdAt ? formatDateTime(run.createdAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Image lightbox with inline label actions */}
      {viewingItem && (
        <ImageModal
          isOpen={!!viewingItem}
          onClose={() => setViewingItem(null)}
          imageUrl={getAlertImageUrl(viewingItem, true)}
          fallbackUrl={getThumbnailUrl(viewingItem)}
          title={`${(viewingItem.subType || PPE_TYPE).replace(/_/g, ' ')} — ${formatConfidence(viewingItem.confidence)}`}
          subtitle={viewingItem.sample ? `Current label: ${labelText(viewingItem.sample.label)}` : 'Not labeled yet'}
          actions={(
            <>
              <button
                onClick={() => labelItem(viewingItem, 'FALSE_POSITIVE')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/30 text-emerald-200 hover:bg-emerald-500/40 transition-colors text-sm font-medium"
                title="Vest was present — false positive (F)"
              >
                <CheckCircleIcon className="w-4 h-4" /> Vest present (FP)
              </button>
              <button
                onClick={() => labelItem(viewingItem, 'CORRECT')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 text-red-200 hover:bg-red-500/30 transition-colors text-sm font-medium"
                title="No vest — real violation (R)"
              >
                <NoSymbolIcon className="w-4 h-4" /> No vest (real)
              </button>
            </>
          )}
          metadata={{
            confidence: viewingItem.confidence,
            detectedAt: viewingItem.detectedAt,
            camera: cameraName,
            type: viewingItem.subType,
          }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, dot }) {
  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <span className="stat-card-label">{label}</span>
        <div className={clsx('w-2 h-2 rounded-full', dot)}></div>
      </div>
      <p className="stat-card-value">{value ?? 0}</p>
    </div>
  );
}

function ReviewCard({ item, onLabel, onViewImage, isPending }) {
  const label = item.sample?.label || null;
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white flex flex-col">
      <AlertThumbnail
        imageUrl={getThumbnailUrl(item)}
        alt={`${item.subType || 'vest'} snapshot`}
        size="full"
        onClick={onViewImage}
        showPlaceholder
        className="w-full aspect-video object-cover cursor-pointer"
      />
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-800">{formatConfidence(item.confidence)} conf</span>
          {label && (
            <span className={clsx(
              'text-xs font-semibold px-2 py-0.5 rounded-full border',
              label === 'FALSE_POSITIVE'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-red-50 text-red-700 border-red-200'
            )}>
              {labelText(label)}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-400">{item.detectedAt ? formatDateTime(item.detectedAt) : '—'}</p>
        <div className="mt-auto grid grid-cols-2 gap-2">
          <button
            onClick={() => onLabel('FALSE_POSITIVE')}
            disabled={isPending}
            className={clsx(
              'flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50',
              label === 'FALSE_POSITIVE'
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50'
            )}
          >
            <CheckCircleIcon className="w-4 h-4" /> Vest present
          </button>
          <button
            onClick={() => onLabel('CORRECT')}
            disabled={isPending}
            className={clsx(
              'flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-50',
              label === 'CORRECT'
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-white text-red-700 border-red-300 hover:bg-red-50'
            )}
          >
            <NoSymbolIcon className="w-4 h-4" /> No vest
          </button>
        </div>
      </div>
    </div>
  );
}

function StageBadge({ stage }) {
  const s = (stage || '').toUpperCase();
  const cls = s === 'ACTIVE'
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : s === 'SHADOW'
      ? 'bg-amber-100 text-amber-700 border-amber-200'
      : 'bg-slate-100 text-slate-600 border-slate-200';
  return <span className={clsx('badge border', cls)}>{s || 'UNKNOWN'}</span>;
}

function RunStatusBadge({ status }) {
  const s = (status || '').toUpperCase();
  const cls = s === 'SUCCEEDED'
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : s === 'RUNNING' || s === 'PENDING'
      ? 'bg-amber-100 text-amber-700 border-amber-200'
      : s === 'FAILED'
        ? 'bg-red-100 text-red-700 border-red-200'
        : 'bg-slate-100 text-slate-600 border-slate-200';
  return <span className={clsx('badge border', cls)}>{s || 'UNKNOWN'}</span>;
}

function labelText(label) {
  if (label === 'FALSE_POSITIVE') return 'Vest present (FP)';
  if (label === 'CORRECT') return 'No vest (real)';
  return label || '—';
}

function formatMetrics(metrics) {
  if (!metrics || typeof metrics !== 'object') return '—';
  const parts = Object.entries(metrics)
    .filter(([, v]) => typeof v === 'number')
    .slice(0, 3)
    .map(([k, v]) => `${k}: ${Number.isInteger(v) ? v : v.toFixed(2)}`);
  return parts.length ? parts.join(', ') : '—';
}
